<template>
  <Layout
    class="onboarding-books"
    :title="page.title"
    :subtitle="`To help you ${booksSubtitlePhrase} ...`"
    :reverseTitleSubtitle="true"
  >
    <div class="onboarding-books__cover-section">
      <Transition name="fade" mode="out-in">
        <div :key="currentBook" class="onboarding-books__cover-wrapper">
          <svg
            class="onboarding-books__cover-bg-svg"
            width="310"
            height="240"
            viewBox="0 0 310 240"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              opacity="0.15"
              d="M0.120931 34.5258C-0.955975 25.7551 5.28107 17.772 14.0518 16.6951L149.038 0.120879C157.809 -0.956028 165.792 5.28102 166.869 14.0517L190.268 204.621C191.345 213.391 185.107 221.374 176.337 222.451L41.3505 239.025C32.5798 240.102 24.5967 233.865 23.5198 225.095L0.120931 34.5258Z"
              fill="#FFC940"
            />
            <rect
              opacity="0.15"
              width="168"
              height="224"
              rx="16"
              transform="matrix(0.992546 0.121869 0.121869 -0.992546 117.171 220.502)"
              fill="#FFC940"
            />
          </svg>
          <img
            :src="view.getBookCover(currentBook)"
            :alt="currentBook"
            width="223"
            height="298"
            class="onboarding-books__cover"
          />
        </div>
      </Transition>
    </div>

    <div class="onboarding-books__controls-wrapper">
      <div class="onboarding-books__controls">
        <button
          class="onboarding-books__button onboarding-books__button--negative"
          @click.prevent="nextBook(-1)"
        >
          <span class="iconfont idislike"></span>
        </button>
        <button
          class="onboarding-books__button onboarding-books__button--neutral"
          @click.prevent="nextBook(0)"
        >
          <img src="@/assets.app/images/onboarding/shrug-emoji.png" alt="" />
        </button>
        <button
          class="onboarding-books__button onboarding-books__button--positive"
          @click.prevent="nextBook(1)"
        >
          <span class="iconfont ilike"></span>
        </button>
      </div>

      <div class="onboarding-books__controls-labels">
        <p class="onboarding-books__controls-label">Not interested</p>
        <p class="onboarding-books__controls-label">Interested</p>
      </div>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import type {
  BooksChoice,
  PersonalizedBooksPage,
} from '@/models/onboarding.types'
import {
  getOnboardingBooks,
  getOnboardingBooksSubtitle,
} from '@/models/onboarding.personalization'

import Layout from '../Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as PersonalizedBooksPage
})

const currentChoices = computed(() => {
  return props.view.currentChoice as BooksChoice
})

const books = computed(() => {
  return getOnboardingBooks(props.view.subjectInterestsYou)
})

const booksSubtitlePhrase = computed(() => {
  return getOnboardingBooksSubtitle(props.view.subjectInterestsYou)
})

const currentBook = computed(() => {
  return books.value[props.view.currentSubpageIndex] ?? ''
})

function nextBook(score: number): void {
  props.view.setCurrentChoice([
    ...currentChoices.value,
    { id: currentBook.value, score },
  ])

  const nextBookExists = props.view.nextSubpage()

  if (!nextBookExists) {
    props.view.goNextPage()
  }
}

function preloadBookCovers(): void {
  for (const slug of books.value) {
    const image = document.createElement('img')
    image.src = props.view.getBookCover(slug)
  }
}

onMounted(() => {
  props.view.setCurrentChoice([])
  preloadBookCovers()
})
</script>

<style lang="scss" scoped>
.onboarding-books {
  padding-bottom: var(--onboarding-padding);

  :deep(.onboarding-layout__text) {
    margin-bottom: 18px;
  }

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    gap: clamp(8px, 2vh, 16px);
  }

  &__cover-section {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    @media only screen and (min-height: 813px) {
      flex: 0 1 auto;
      max-height: min(380px, 48vh);
    }
  }

  &__cover-wrapper {
    position: relative;
    display: flex;
    flex: 0 1 auto;
    min-height: 200px;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 36px;
    border-radius: 14px;
    overflow: hidden;
    @media only screen and (max-height: 710px) {
      padding: 16px;
    }
    @media (min-width: $w-medium-b) {
      max-width: 327px;
      max-height: 368px;
      margin-inline: auto;
    }

    @media (min-width: $w-pc-b) {
      max-width: 271px;
      max-height: 306px;
    }
  }

  &__cover-bg-svg {
    position: absolute;
    pointer-events: none;
    z-index: 0;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: min(100%, 310px);
    height: auto;
    max-height: 100%;
  }

  &__cover {
    width: auto;
    max-width: 223px;
    max-height: 100%;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    z-index: 2;
    @media only screen and (min-height: 813px) {
      width: 223px;
    }
    @media (min-width: $w-pc-b) {
      max-width: 183px;
    }
  }

  &__cover--background {
    position: absolute;
    pointer-events: none;
    position: absolute;
    z-index: 1;
    inset: 0;
    transform: scale(5);
    filter: blur(30px) saturate(1.3);
    opacity: 0.3;
  }

  &__controls-wrapper {
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    flex-shrink: 0;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 4px);
    background: $c-bg;
  }

  &__controls-labels {
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin-top: 8px;
  }

  &__controls-label {
    font-size: 14px;
    line-height: 24px;
    letter-spacing: -0.02em;
    color: $c-black-6;
    margin: 0;
  }

  &__controls {
    position: relative;
    display: flex;
    width: 100%;
    background: $c-white;
    padding-block: 16px;
    border: 1px solid $c-border-2;
    border-radius: 14px;
    @media only screen and (max-height: 500px) {
      padding-block: 12px;
    }
  }

  &__button {
    --icon-size: 40px;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    border-inline: 1px solid transparent;

    img {
      width: var(--icon-size);
    }

    .iconfont {
      width: var(--icon-size);
      height: var(--icon-size);
      font-size: var(--icon-size);
    }

    &:not(:last-child) {
      border-right: 1px solid $c-border-2;
    }
  }

  &__button--positive {
    color: #7ed86f;
  }

  &__button--negative {
    color: #ea726d;
  }
}
</style>
